import React, { useMemo, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { royalcaninDogProducts, royalcaninCatProducts, eukanubaProducts, nexgardProducts, simparicaProducts, bravectoProducts, purinaProducts, bobmartinProducts, formatZAR } from '../Components/ProductRetrieval'
import { getFilterOptions, filterProducts, sortProducts } from '../Utils/Product_filter_util'
import { groupByFormula } from '../Utils/weightVariantsUtil'
import Pagination from '../Components/Pagination/Pagination'
import { useCart } from '../Components/Cart'
import { useFilterParams, FilterBar } from '../Components/FilterBar'

const ITEMS_PER_PAGE = 20
const PET_TYPE_OPTIONS = ['All', 'Dog', 'Cat']

const PetFood = () => {
  const { searchParams, setParam, setCurrentPage, resetFilters } = useFilterParams()
  const [justAddedId, setJustAddedId] = useState(null)
  const [selectedWeights, setSelectedWeights] = useState({})
  const { addToCart } = useCart()

  const searchTerm = searchParams.get('q') || ''
  const sortBy = searchParams.get('sort') || 'name-asc'
  const filterPetType = searchParams.get('petType') || 'All'
  const filterBrand = searchParams.get('brand') || 'All'
  const filterCategory = searchParams.get('category') || 'All'
  const filterAge = searchParams.get('age') || 'All'
  const filterBreed = searchParams.get('breed') || 'All'
  const filterNeeds = searchParams.get('needs') || 'All'
  const filterSize = searchParams.get('size') || 'All'
  const filterSterilised = searchParams.get('sterilised') || 'All'
  const filterProductSize = searchParams.get('productSize') || 'All'
  const currentPage = parseInt(searchParams.get('page')) || 1

  const allProducts = useMemo(() => [
    ...royalcaninDogProducts.map((p) => ({ ...p, petType: 'Dog' })),
    ...royalcaninCatProducts.map((p) => ({ ...p, petType: 'Cat' })),
    ...eukanubaProducts.map((p) => ({ ...p, petType: 'Dog' })),
    ...nexgardProducts.map((p) => ({ ...p, petType: p.additionalInfo?.pet_type || 'Dog' })),
    ...simparicaProducts.map((p) => ({ ...p, petType: p.additionalInfo?.pet_type || 'Dog' })),
    // ...beenoProducts.map((p) => ({ ...p, petType: p.additionalInfo?.pet_type || 'Dog' })),
    ...bravectoProducts.map((p) => ({ ...p, petType: p.additionalInfo?.pet_type || 'Dog' })),
    ...purinaProducts.map((p) => ({ ...p, petType: p.additionalInfo?.pet_type || 'Dog' })),
    ...bobmartinProducts.map((p) => ({ ...p, petType: p.additionalInfo?.pet_type || 'Dog' })),
  ], [])

  const brandOptions = useMemo(() => getFilterOptions(allProducts, 'brand'), [allProducts])
  const categoryOptions = useMemo(() => getFilterOptions(allProducts, 'category'), [allProducts])
  const ageOptions = useMemo(() => getFilterOptions(allProducts, 'age'), [allProducts])
  const breedOptions = useMemo(() => getFilterOptions(allProducts, 'breed'), [allProducts])
  const needsOptions = useMemo(() => getFilterOptions(allProducts, 'needs'), [allProducts])
  const sizeOptionsRaw = useMemo(() => getFilterOptions(allProducts, 'size'), [allProducts])
  const sizeOptions = useMemo(() => sizeOptionsRaw.map(opt => opt === 'All' ? opt : { value: opt, label: String(opt).split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ') }), [sizeOptionsRaw])
  const sterilisedOptions = useMemo(() => getFilterOptions(allProducts, 'sterilised'), [allProducts])
  const productSizeOptions = useMemo(() => getFilterOptions(allProducts, 'productSize'), [allProducts])

  const handleAddToCart = useCallback((variant) => {
    addToCart(variant.id, variant.name)
    setJustAddedId(variant.id)
    setTimeout(() => setJustAddedId(null), 2000)
  }, [addToCart])

  const filteredProducts = useMemo(() => {
    const filters = {
      petType: filterPetType,
      brand: filterBrand,
      category: filterCategory,
      age: filterAge,
      breed: filterBreed,
      needs: filterNeeds,
      size: filterSize,
      sterilised: filterSterilised,
      productSize: filterProductSize,
    }
    let products = filterProducts(allProducts, filters)
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      products = products.filter(p => p.name.toLowerCase().includes(term))
    }
    return sortProducts(products, sortBy)
  }, [filterPetType, filterBrand, filterCategory, filterAge, filterBreed, filterNeeds, filterSize, filterSterilised, filterProductSize, sortBy, searchTerm, allProducts])

  const groupedProducts = useMemo(() => {
    const groups = groupByFormula(filteredProducts)
    groups.sort((a, b) => {
      if (sortBy === 'name-desc') return b.baseName.localeCompare(a.baseName)
      if (sortBy === 'price-asc') {
        const aMin = Math.min(...a.variants.map(v => v.price))
        const bMin = Math.min(...b.variants.map(v => v.price))
        return aMin - bMin
      }
      if (sortBy === 'price-desc') {
        const aMin = Math.min(...a.variants.map(v => v.price))
        const bMin = Math.min(...b.variants.map(v => v.price))
        return bMin - aMin
      }
      return a.baseName.localeCompare(b.baseName)
    })
    return groups
  }, [filteredProducts, sortBy])

  const activeFilterCount = [filterPetType, filterBrand, filterCategory, filterAge, filterBreed, filterNeeds, filterSize, filterSterilised, filterProductSize]
    .filter((v) => v !== 'All').length

  const totalPages = Math.ceil(groupedProducts.length / ITEMS_PER_PAGE)
  const paginatedGroups = groupedProducts.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  )

  const handleClearFilters = useCallback(() => {
    resetFilters(['petType', 'brand', 'category', 'age', 'breed', 'needs', 'size', 'sterilised', 'productSize'])
  }, [resetFilters])

  return (
    <div className="min-h-screen bg-[#faf9fe] text-gray-900">
      <section className="border-b border-gray-200 bg-gradient-to-br from-violet-700 via-violet-600 to-purple-800 px-4 py-14 text-white sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <p className="text-sm font-semibold uppercase tracking-widest text-violet-200">
            ArrAndArr
          </p>
          <h1 className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl">
            Search Products
          </h1>
          <div className="mt-4 flex gap-2">
            {PET_TYPE_OPTIONS.map((type) => (
              <button
                key={type}
                onClick={() => setParam('petType', type === 'All' ? 'All' : type)}
                className={`rounded-full px-5 py-1.5 text-sm font-semibold transition ${
                  filterPetType === type
                    ? 'bg-white text-violet-700 shadow'
                    : 'bg-violet-500/30 text-white hover:bg-violet-500/50'
                }`}
              >
                {type === 'All' ? 'All Pets' : type}
              </button>
            ))}
          </div>
          <p className="mt-4 max-w-2xl text-lg text-violet-200">
            Browse and search our full range of premium pet food and care products
            for every breed, age, and size.
          </p>
          <p className="mt-3 text-sm text-violet-200">
            All prices include VAT. Prices subject to change.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <FilterBar
          filters={[
            { key: 'brand', label: 'Brand', options: brandOptions, value: filterBrand, onChange: (v) => setParam('brand', v) },
            { key: 'category', label: 'Category', options: categoryOptions, value: filterCategory, onChange: (v) => setParam('category', v) },
            { key: 'age', label: 'Life Stage', options: ageOptions, value: filterAge, onChange: (v) => setParam('age', v) },
            { key: 'breed', label: 'Breed', options: breedOptions, value: filterBreed, onChange: (v) => setParam('breed', v) },
            { key: 'needs', label: 'Care/Health needs', options: needsOptions, value: filterNeeds, onChange: (v) => setParam('needs', v) },
            { key: 'size', label: 'Breed Size', options: sizeOptions, value: filterSize, onChange: (v) => setParam('size', v) },
            { key: 'sterilised', label: 'Sterilised', options: sterilisedOptions, value: filterSterilised, onChange: (v) => setParam('sterilised', v) },
            { key: 'productSize', label: 'Product Size', options: productSizeOptions, value: filterProductSize, onChange: (v) => setParam('productSize', v) },
          ]}
          sortBy={sortBy}
          onSortChange={(v) => setParam('sort', v)}
          searchTerm={searchTerm}
          onSearchChange={(v) => setParam('q', v)}
          searchPlaceholder="Search all products..."
          onClear={handleClearFilters}
          activeFilterCount={activeFilterCount}
          resultCount={paginatedGroups.length}
          totalCount={groupedProducts.length}
          resultUnit="products"
          colorScheme="violet"
        />

        <Pagination
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          colorScheme="violet"
        /> <br/>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {paginatedGroups.map((group) => {
            const variants = group.variants
            const first = variants[0]
            const selectedId = selectedWeights[group.baseName] || first.id
            const selectedVariant = variants.find(v => v.id === selectedId) || first
            const isOutOfStock = selectedVariant.inStock === false || selectedVariant.inStock === 0
            const isPreorder = selectedVariant.isPreOrder === true || selectedVariant.inStock === 2
            const hasWeights = variants.length > 1

            return (
              <article
                key={group.baseName}
                className="group flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-md"
              >
                <Link to={`/Product/${first.id}`} className="flex flex-1 flex-col">
                  <div className="relative aspect-square bg-gradient-to-br from-gray-50 to-gray-100">
                    {first.image ? (
                      <img
                        src={first.image}
                        alt={group.baseName}
                        className={`h-full w-full object-contain max-h-[280px] ${isOutOfStock ? 'grayscale' : ''}`}
                      />
                    ) : (
                      <div className="flex h-full flex-col items-center justify-center px-4 text-center">
                        <span className="text-sm font-medium text-gray-500">
                          No image available
                        </span>
                      </div>
                    )}
                    {isOutOfStock && (
                      <span className="absolute left-3 top-3 rounded-full bg-gray-600 px-2.5 py-1 text-xs font-semibold text-white">
                        Out of stock
                      </span>
                    )}
                    {isPreorder && (
                      <span className="absolute left-3 top-3 rounded-full bg-amber-500 px-2.5 py-1 text-xs font-semibold text-white">
                        Pre-order
                      </span>
                    )}
                  </div>

                  <div className="flex flex-1 flex-col p-4 text-left">
                    <h2 className={`line-clamp-2 text-base font-semibold ${
                      isOutOfStock ? 'text-gray-500' : 'text-gray-900 group-hover:text-violet-600'
                    }`}>
                      {group.baseName}
                    </h2>
                    {first.description && (
                      <p className="mt-1.5 line-clamp-2 text-xs text-gray-500">
                        {first.description}
                      </p>
                    )}
                    <p className={`mt-auto pt-3 text-lg font-bold ${isOutOfStock ? 'text-gray-400' : 'text-gray-900'}`}>
                      {hasWeights ? formatZAR(selectedVariant.price) : formatZAR(first.price)}
                    </p>
                  </div>
                </Link>
                <div className="flex flex-col gap-2 px-4 pb-4">
                  {hasWeights && (
                    <div className="flex flex-wrap gap-1.5">
                      {variants.map((v) => {
                        const label = v.name.match(/\(([^)]+)\)$/)?.[1] || `${v.weight}kg`
                        return (
                          <button
                            key={v.id}
                            onClick={() => setSelectedWeights(prev => ({ ...prev, [group.baseName]: v.id }))}
                            className={`rounded px-2.5 py-1 text-xs font-semibold transition ${
                              selectedVariant.id === v.id
                                ? 'bg-violet-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            } ${(!v.inStock && v.inStock !== 1) ? 'opacity-40 cursor-not-allowed' : ''}`}
                          >
                            {label}
                          </button>
                        )
                      })}
                    </div>
                  )}
                  <button
                    onClick={() => handleAddToCart(selectedVariant)}
                    disabled={isOutOfStock}
                    className={`w-full rounded-lg px-4 py-2 text-sm font-semibold text-white transition ${
                      isOutOfStock
                        ? 'bg-gray-400 cursor-not-allowed'
                        : justAddedId === selectedVariant.id
                        ? 'bg-violet-700'
                        : 'bg-violet-600 hover:bg-violet-700'
                    }`}
                  >
                    {justAddedId === selectedVariant.id
                      ? 'Added to cart'
                      : isOutOfStock
                      ? 'Out of stock'
                      : 'Add to cart'}
                  </button>
                </div>
              </article>
            )
          })}
        </div>

        <Pagination
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          colorScheme="violet"
        />
      </section>
    </div>
  )
}

export default PetFood
